import { useState, useMemo, useEffect, useRef } from "react";
import type { DateRange } from "../client/components/DateRangePicker";
import { presetToDateRange } from "../client/utils/dateRange";
import { useAuth } from "wasp/client/auth";
import { getActiveLayout, getAccountChargesByUser, getTptWalletContext, getMyMentors, resetLayoutContent, ensureDashboardDefaultLayout, useQuery, useAction } from "wasp/client/operations";
import { Card, CardContent } from "../client/components/ui/card";
import { useIsDark, useThemeId } from "../client/components/ui/card-section-header";
import { Button } from "../client/components/ui/button";
import { getTradePhase } from "../trading-accounts/tradePhase";
import {
  calculateMetrics,
  netPnl,
} from "../trading-journal/analytics";
import { useAnalyticsData } from "./widgets/useAnalyticsData";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import useTranslation from "../client/hooks/useTranslation";
import useIsMobile from "../client/hooks/useIsMobile";
import type { AccountEvent, FundedEvent, PayoutEvent } from "./TradingCalendar";
import DayDetailModal from "./DayDetailModal";
import { buildAccountEvents, buildFundedEvents, buildPayoutEvents, buildViolationsByDate } from "./calendarEvents";
import { Sparkles, Loader2, Layers, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PageTransition } from "../client/components/ui/motion";
import type { AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import { BeamButton } from "../client/components/ui/beam-button";
import { DashboardDataProvider, type DashboardData } from "./widgets/DashboardDataContext";
import { WidgetDataProvider } from "./widgets/WidgetDataContext";
import DashboardLayout from "./widgets/DashboardLayout";
import EditModeBar from "./widgets/EditModeBar";
import AddWidgetPicker from "./widgets/AddWidgetPicker";
import ResetLayoutDialog from "./widgets/ResetLayoutDialog";
import { useActiveLayout, compactSlots } from "./widgets/useActiveLayout";
import { useLayoutsLibrary } from "./widgets/useLayoutsLibrary";
import { getDefaultLayout, withDefaultLayoutFallback, ANALYTICS_WIDGET_KEYS, PROPFIRM_WIDGET_KEYS, PROPFIRM_ONLY_WIDGET_KEYS, metricsForWidgetKeys } from "./widgets/registry";
import { TOPBAR_WIDGET_KEYS } from "./widgets/AddWidgetPicker";
import {
  getDefaultDateRange,
  getDefaultAccountFilter,
  getDefaultStrategyIds,
  getDefaultPfmFilter,
  getDefaultPhaseFilter,
  serializeDateRange,
  serializeAccountFilter,
  serializeStrategyIds,
  serializePfmFilter,
  serializePhaseFilter,
} from "../custom-pages/topbarDefaults";
import { useToast } from "../client/hooks/use-toast";
import type { LayoutSnapshot, SlotPlacement, WidgetKey } from "./widgets/types";
import LayoutLibraryModal from "./widgets/LayoutLibraryModal";
import { SortableTopbarSlot, TopbarDndWrapper } from "./widgets/TopbarSlotRenderer";
import TopbarFilterBar from "./widgets/TopbarFilterBar";
import { CURRENCY } from "../shared/currency";
import { buildAccountCalendarEvents } from "../prop-firm-manager/accountCalendarEvents";
import { chargeCalendarInstances } from "../prop-firm-manager/chargeSchedule";
import type { CalendarEvent } from "../prop-firm-manager/PropFirmCalendar";
import { trackWidgetAdded, trackWidgetRemoved, trackLayoutSaved } from '../client/utils/posthog';
import EdgyHeaderButton from "../edgy/EdgyHeaderButton";
import { isActivePropFirmAccount } from "../prop-firm-manager/pfmPeriod";

function layoutHasAnyKey(layout: LayoutSnapshot | null, keys: WidgetKey[]): boolean {
  if (!layout) return false;
  return layout.slots.some((slot) => keys.includes(slot.widgetKey));
}

export interface DashboardViewProps {
  trades: any[];
  accounts: any[];
  sessions: any[];
  routines: any[];
  goals: any[];
  accountGroups: any[];
  strategies: any[];
  readOnly: boolean;
  /**
   * Les totaux affiches sont-ils potentiellement incomplets (sync initiale en
   * cours, ou dernier cycle bride par le quota) ? Les vues qui somment des
   * trades doivent le DIRE au lieu de presenter un chiffre partiel comme un
   * fait. Defaut false : une vue qui ne le fournit pas (mentor) n'avertit rien.
   */
  totalsArePartial?: boolean;
  isFreePlan?: boolean;
  showPlanModal?: boolean;
  // Callbacks for mutative actions — only called when readOnly=false
  onAddTrade?: () => void;
  onPromoteToFunded?: (account: any) => void;
  onCompleteRoutine?: (routine: any) => void;
  onOpenPlanModal?: () => void;
}

export default function DashboardView({
  trades: allTradesProp,
  accounts: dashboardAccounts,
  sessions,
  routines,
  goals,
  accountGroups: dashboardGroupsProp,
  strategies: dashboardStrategiesProp,
  readOnly,
  totalsArePartial = false,
  isFreePlan = false,
  showPlanModal = false,
  onAddTrade,
  onPromoteToFunded,
  onCompleteRoutine,
  onOpenPlanModal,
}: DashboardViewProps) {
  const t = useTranslation();
  const isDark = useIsDark();
  const themeId = useThemeId();

  const { data: authUser } = useAuth();
  const { data: mentors = [] } = useQuery(getMyMentors, undefined, { enabled: !readOnly });
  const hasMentor = !readOnly && (mentors as any[]).length > 0;

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: "all" });
  const [strategyIds, setStrategyIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    presetToDateRange(readOnly ? "all_time" : "last_30_days")
  );
  const filterInitialized = useRef(false);
  const [chartMode, setChartMode] = useState<"pnl" | "networth">(() =>
    (localStorage.getItem("dashboard_chart_mode") as "pnl" | "networth") ?? "networth"
  );
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [pfmTemplateFilter, setPfmTemplateFilter] = useState<string | null>(null);
  const [accountStatusFilter, setAccountStatusFilter] = useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));
  const [showImportModal, setShowImportModal] = useState(false);
  const [showAddPayoutModal, setShowAddPayoutModal] = useState(false);

  // Derive startDate/endDate from the selected calendar month
  const { calendarStartDate, calendarEndDate } = useMemo(() => {
    const monthStart = startOfMonth(selectedMonth);
    const monthEnd = endOfMonth(selectedMonth);
    return {
      calendarStartDate: startOfWeek(monthStart, { weekStartsOn: 1 }),
      calendarEndDate: endOfWeek(monthEnd, { weekStartsOn: 1 }),
    };
  }, [selectedMonth]);

  // Dates for stats cards (period filter)
  const statsStartDate = dateRange.from ?? undefined;
  const statsEndDate = dateRange.to ?? undefined;

  // Compute the set of account IDs that match accountFilter + pfmTemplateFilter
  const filteredAccountIds = useMemo<Set<string> | null>(() => {
    const accounts = (dashboardAccounts || []) as any[];

    // Resolve all template IDs sharing the same name as the selected template (handles system vs custom duplicates)
    const pfmTemplateIds: Set<string> | null = (() => {
      if (!pfmTemplateFilter) return null;
      const selectedName = accounts.find((a: any) => a.propFirmTemplateId === pfmTemplateFilter)?.propFirmTemplate?.name;
      if (!selectedName) return new Set([pfmTemplateFilter]);
      const ids = accounts
        .filter((a: any) => a.propFirmTemplate?.name === selectedName)
        .map((a: any) => a.propFirmTemplateId as string);
      return new Set(ids);
    })();

    // Apply pfmTemplateFilter
    let pool = pfmTemplateIds
      ? accounts.filter((a: any) => pfmTemplateIds.has(a.propFirmTemplateId))
      : accounts;

    // Apply accountFilter
    if (accountFilter.type === "none") return new Set();
    if (accountFilter.type === "account") {
      const found = pool.find((a: any) => a.id === accountFilter.id);
      return found ? new Set([found.id]) : new Set();
    }
    if (accountFilter.type === "group") {
      const groupAccountIds = (dashboardGroupsProp || []).find((g: any) => g.id === accountFilter.id)
        ?.accounts?.map((a: any) => a.id) ?? [];
      const ids = pool.filter((a: any) => groupAccountIds.includes(a.id)).map((a: any) => a.id);
      return new Set(ids);
    }
    if (accountFilter.type === "all-inactive") {
      return null; // null = no account restriction (all accounts including inactive)
    }
    // "all": only active accounts (exclude inactive status even if isActive flag wasn't set to false)
    return new Set(pool.filter((a: any) => a.isActive !== false && a.status !== "inactive").map((a: any) => a.id));
  }, [dashboardAccounts, dashboardGroupsProp, accountFilter, pfmTemplateFilter, accountStatusFilter]);

  // Map accountId → account for phase filtering
  const accountMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const a of (dashboardAccounts || []) as any[]) map.set(a.id, a);
    return map;
  }, [dashboardAccounts]);

  // Filter trades for calendar (by month range + accountIds + strategyIds + phase)
  const trades = useMemo(() => {
    let filtered = allTradesProp;
    if (calendarStartDate && calendarEndDate) {
      filtered = filtered.filter((t: any) => {
        const d = new Date(t.date);
        return d >= calendarStartDate && d <= calendarEndDate;
      });
    }
    if (filteredAccountIds !== null) {
      filtered = filtered.filter((t: any) => filteredAccountIds.has(t.accountId));
    }
    if (accountStatusFilter === "pa" || accountStatusFilter === "challenge") {
      const targetPhase = accountStatusFilter === "pa" ? "funded" : "challenge";
      filtered = filtered.filter((t: any) => {
        const account = accountMap.get(t.accountId);
        if (!account) return false;
        return getTradePhase({ date: new Date(t.date) }, account) === targetPhase;
      });
    }
    if (strategyIds.length > 0) {
      filtered = filtered.filter((t: any) =>
        t.strategies?.some((s: any) => strategyIds.includes(s.id))
      );
    }
    return filtered;
  }, [allTradesProp, calendarStartDate, calendarEndDate, filteredAccountIds, accountStatusFilter, accountMap, strategyIds]);

  // Filter trades for stats (by period + accountIds + strategyIds + phase)
  const statsTradesList = useMemo(() => {
    let filtered = allTradesProp;
    if (statsStartDate) {
      filtered = filtered.filter((t: any) => {
        const d = new Date(t.date);
        return d >= statsStartDate && (!statsEndDate || d <= statsEndDate);
      });
    }
    if (filteredAccountIds !== null) {
      filtered = filtered.filter((t: any) => filteredAccountIds.has(t.accountId));
    }
    if (accountStatusFilter === "pa" || accountStatusFilter === "challenge") {
      const targetPhase = accountStatusFilter === "pa" ? "funded" : "challenge";
      filtered = filtered.filter((t: any) => {
        const account = accountMap.get(t.accountId);
        if (!account) return false;
        return getTradePhase({ date: new Date(t.date) }, account) === targetPhase;
      });
    }
    if (strategyIds.length > 0) {
      filtered = filtered.filter((t: any) =>
        t.strategies?.some((s: any) => strategyIds.includes(s.id))
      );
    }
    return filtered;
  }, [allTradesProp, statsStartDate, statsEndDate, filteredAccountIds, accountStatusFilter, accountMap, strategyIds]);

  const metrics = calculateMetrics(statsTradesList);
  const activeRoutines = routines?.filter((r) => r.isActive) || [];
  const activeGoals = goals?.filter((g) => !g.isCompleted) || [];

  // Equity curve data — uses statsTradesList
  const equityCurveData = useMemo(() => {
    const allAccounts = (dashboardAccounts || []) as any[];
    // Filter by filteredAccountIds (pfmTemplateFilter + accountFilter intersection), then exclude "none"
    const activeAccounts = accountFilter.type === "none"
      ? []
      : filteredAccountIds !== null
        ? allAccounts.filter((a: any) => filteredAccountIds.has(a.id))
        : allAccounts;
    // Resolve starting balance per account: explicit initialBalance > tier minBalance/accountSize > 0
    const initialBalance = activeAccounts.reduce((s: number, a: any) => {
      if (a.initialBalance && a.initialBalance > 0) return s + a.initialBalance;
      const tierBase = a.activeTier?.accountSize ?? a.activeTier?.minBalance ?? 0;
      if (tierBase > 0) return s + tierBase;
      return s;
    }, 0) || null;

    const closedTrades = [...statsTradesList]
      .filter((t) => t.pnl !== null && t.pnl !== undefined)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    if (closedTrades.length === 0) return { points: [] as { x: number; y: number }[], pctChange: 0, totalPnl: 0, initialBalance };

    const startValue = chartMode === "networth" ? (initialBalance ?? 0) : 0;
    let running = startValue;
    const points: { x: number; y: number }[] = startValue !== 0
      ? [{ x: new Date(closedTrades[0].date).getTime() - 1, y: startValue }]
      : [];
    for (const t of closedTrades) {
      running += netPnl(t);
      points.push({ x: new Date(t.date).getTime(), y: parseFloat(running.toFixed(2)) });
    }
    const totalPnl = running - startValue;
    const pctChange = chartMode === "networth" && initialBalance && initialBalance !== 0
      ? (totalPnl / initialBalance) * 100
      : 0;
    const xMin = points.length > 0 ? points[0].x : undefined;
    const xMax = points.length > 0 ? points[points.length - 1].x : undefined;
    return { points, pctChange, initialBalance, totalPnl, xMin, xMax };
  }, [statsTradesList, dashboardAccounts, filteredAccountIds, accountFilter, chartMode]);

  // Événements du calendrier : calcul partagé avec le Lite (calendarEvents.ts).
  const calendarAccounts = useMemo(() => {
    const accounts = (dashboardAccounts || []) as any[];
    return accountFilter.type === "none"
      ? []
      : accountFilter.type === "account"
      ? accounts.filter((a: any) => a.id === accountFilter.id)
      : accountFilter.type === "group"
      ? accounts.filter((a: any) => a.groupId === accountFilter.id)
      : accounts;
  }, [dashboardAccounts, accountFilter]);

  const accountEvents = useMemo<AccountEvent[]>(() => buildAccountEvents(calendarAccounts), [calendarAccounts]);
  const fundedEvents = useMemo<FundedEvent[]>(() => buildFundedEvents(calendarAccounts), [calendarAccounts]);
  const payoutEvents = useMemo<PayoutEvent[]>(() => buildPayoutEvents(calendarAccounts), [calendarAccounts]);

  // Violations by date
  const violationsByDate = useMemo(() => {
    const accounts = (dashboardAccounts || []) as any[];
    const filtered = accountFilter.type === "none"
      ? []
      : accountFilter.type === "account"
      ? accounts.filter((a: any) => a.id === accountFilter.id)
      : accountFilter.type === "group"
      ? accounts.filter((a: any) => a.groupId === accountFilter.id)
      : accountFilter.type === "all-inactive"
      ? accounts
      : accounts.filter((a: any) => a.isActive !== false);
    return buildViolationsByDate(filtered, t.dashboard.calendar.violations);
  }, [dashboardAccounts, accountFilter]);

  // Pending routines sorted by priority
  const pendingRoutines = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diffToMonday);
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

    return activeRoutines.filter((r) => {
      if (r.frequency === "daily") {
        return !r.routineCompletions.some((c: any) => {
          const d = new Date(c.date); d.setHours(0, 0, 0, 0);
          return d.getTime() === today.getTime();
        });
      }
      if (r.frequency === "weekly") {
        return !r.routineCompletions.some((c: any) => new Date(c.date) >= monday && new Date(c.date) <= today);
      }
      if (r.frequency === "monthly") {
        return !r.routineCompletions.some((c: any) => new Date(c.date) >= monthStart && new Date(c.date) <= today);
      }
      return false;
    });
  }, [activeRoutines]);

  // Top goals sorted by urgency then progress
  const topGoals = useMemo(() => {
    const today = new Date();
    return activeGoals
      .filter((g) => g.targetValue > 0 && g.type !== "custom")
      .map((g) => {
        const progress = Math.min((g.currentValue / g.targetValue) * 100, 100);
        const daysLeft = g.deadline
          ? Math.ceil((new Date(g.deadline).getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
          : null;
        const urgencyBonus = daysLeft !== null && daysLeft >= 0 && daysLeft <= 7 ? 40 : 0;
        return { goal: g, progress, daysLeft, score: progress + urgencyBonus };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [activeGoals]);

  // --- Dashboard layout system (rows + slots) ---
  const { data: serverLayout, refetch: refetchLayout } = useQuery(getActiveLayout);
  const { data: chargesData, isLoading: isLoadingPfm } = useQuery(getAccountChargesByUser);
  const { data: tptWalletContext } = useQuery(getTptWalletContext);
  const charges = (chargesData ?? []) as any[];

  const ensureDashboardFn = useAction(ensureDashboardDefaultLayout);
  const ensureDashboardCalledRef = useRef(false);
  useEffect(() => {
    if (ensureDashboardCalledRef.current || readOnly) return;
    ensureDashboardCalledRef.current = true;
    ensureDashboardFn(undefined).then(() => refetchLayout()).catch(() => {});
  }, []);

  const initialLayout: LayoutSnapshot = useMemo(() => {
    if (!serverLayout) return getDefaultLayout();
    const rows = [...serverLayout.rows].sort((a, b) => a.order - b.order);
    const allSlots: SlotPlacement[] = [];

    // New-style layouts (saved by our global grid system) always have cols=6.
    // Legacy layouts had cols=2,3,4 and row.order was a sequential index.
    const isNewStyle = rows.length === 0 || rows.every((r) => r.cols === 6);

    if (isNewStyle) {
      // row.order IS the global rowStart directly. order=0 is reserved for topbar slots.
      for (const row of rows) {
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: row.order,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
            ...((s as any).placement ? { placement: (s as any).placement } : {}),
            ...((s as any).defaultFilterValue != null ? { defaultFilterValue: (s as any).defaultFilterValue } : {}),
            ...((s as any).viewMode != null ? { viewMode: (s as any).viewMode } : {}),
          });
        }
      }
    } else {
      // Legacy: row.order is a sequential index — reconstruct rowStart cumulatively.
      let cursor = 1;
      for (const row of rows) {
        const maxRowSpan = row.slots.reduce((m: number, s: any) => Math.max(m, s.rowSpan ?? 1), 1);
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: cursor,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
          });
        }
        cursor += maxRowSpan;
      }
    }
    const topbar = allSlots.filter((s) => s.placement === "topbar");
    const grid = allSlots.filter((s) => s.placement !== "topbar");
    return withDefaultLayoutFallback({ slots: [...topbar, ...compactSlots(grid)] }, "dashboard");
  }, [serverLayout]);

  // Sync filter state from topbar slot defaults once serverLayout loads
  useEffect(() => {
    if (filterInitialized.current || !serverLayout || readOnly) return;
    filterInitialized.current = true;
    const slots = initialLayout.slots;
    setDateRange(getDefaultDateRange(slots));
    setAccountFilter(getDefaultAccountFilter(slots));
    setStrategyIds(getDefaultStrategyIds(slots));
    setPfmTemplateFilter(getDefaultPfmFilter(slots));
  }, [serverLayout]);

  const {
    layout,
    addWidget,
    removeWidget,
    setWidgetSize,
    moveWidget,
    swapWidgets,
    flushSave,
    compactAndFlushSave,
    updateSlotDefaultFilterValue,
    updateSlotViewMode,
  } = useActiveLayout(initialLayout, (serverLayout as any)?.id ?? null, undefined, readOnly);

  // Detect which widget categories are present in the active layout
  const hasAnalyticsWidgets = layoutHasAnyKey(layout, ANALYTICS_WIDGET_KEYS);
  const hasPropFirmWidgets = layoutHasAnyKey(layout, PROPFIRM_WIDGET_KEYS);

  const topbarSlots = useMemo(
    () => layout.slots.filter((s) => s.placement === "topbar").sort((a, b) => a.colStart - b.colStart),
    [layout.slots]
  );
  const { toast } = useToast();

  // Calcul analytics à la carte : on ne calcule que les métriques réclamées par
  // les widgets réellement présents dans le layout, et chacune n'est évaluée
  // qu'à sa première lecture (getters paresseux dans useAnalyticsData).
  const analyticsMetrics = useMemo(
    () => metricsForWidgetKeys(layout?.slots.map((s) => s.widgetKey) ?? []),
    [layout?.slots],
  );
  const analyticsData = useAnalyticsData(
    statsTradesList,
    sessions ?? [],
    hasAnalyticsWidgets,
    analyticsMetrics,
  );

  // Lazily compute propFirmData — only when prop firm widgets are in the layout
  const propFirmData = useMemo(() => {
    if (!hasPropFirmWidgets) return undefined;
    const accounts = (dashboardAccounts || []) as any[];
    const chargeList = (charges ?? []) as {
      id: string;
      amount: number;
      frequency: string;
      accountId?: string | null;
      name: string;
      createdAt: Date | string;
      startDate?: Date | string | null;
      endDate?: Date | string | null;
      billingDay?: number | null;
    }[];

    // KPI computation (no period filter — uses all data)
    let totalPayouts = 0;
    let totalExpenses = 0;
    let bestRoiAccount: { name: string; roi: number } | null = null;
    let activeCount = 0;

    for (const acc of accounts) {
      const accPayouts = ((acc.payouts ?? []) as { date: Date | string; amount: number }[]).reduce(
        (s, p) => s + p.amount,
        0,
      );
      totalPayouts += accPayouts;

      const challengeCost = acc.challengeCost ?? 0;
      const activationFee = acc.activationFee ?? 0;
      totalExpenses += challengeCost + activationFee;

      if (isActivePropFirmAccount(acc)) activeCount++;

      const accExpenses = challengeCost + activationFee;
      if (accExpenses > 0) {
        const roi = ((accPayouts - accExpenses) / accExpenses) * 100;
        if (!bestRoiAccount || roi > bestRoiAccount.roi) {
          bestRoiAccount = { name: acc.name, roi };
        }
      }
    }

    // Add charges to expenses
    const linkedCharges = chargeList.filter((c) => c.accountId);
    const globalCharges = chargeList.filter((c) => !c.accountId);
    for (const acc of accounts) {
      const sinceDate = acc.createdAt ? new Date(acc.createdAt) : new Date();
      for (const charge of linkedCharges.filter((c) => c.accountId === acc.id)) {
        totalExpenses += charge.amount;
      }
    }
    if (globalCharges.length > 0) {
      for (const charge of globalCharges) {
        totalExpenses += charge.amount;
      }
    }

    const netProfit = totalPayouts - totalExpenses;
    const roi = totalExpenses > 0 ? (netProfit / totalExpenses) * 100 : 0;

    // Calendar events
    const now = new Date();
    const calendarEvents: CalendarEvent[] = [];
    for (const acc of accounts) {
      calendarEvents.push(...buildAccountCalendarEvents(acc, CURRENCY));
    }
    for (const charge of chargeList) {
      if (charge.accountId) {
        const belongs = accounts.some((a) => a.id === charge.accountId);
        if (!belongs) continue;
      }
      // Même règle que les KPIs (chargeSchedule) : un abonnement fermé ne
      // projette plus aucun prélèvement après sa date de fin.
      const eventType = charge.frequency === "one_time" ? "expense" : "recurring";
      for (const { date, amount } of chargeCalendarInstances(charge, now)) {
        calendarEvents.push({ date, type: eventType, amount, currency: CURRENCY, label: charge.name });
      }
    }

    return {
      kpis: { totalPayouts, totalExpenses, netProfit, roi, activeCount, bestRoi: bestRoiAccount },
      calendarEvents,
      charges: chargeList,
    };
  }, [hasPropFirmWidgets, dashboardAccounts, charges]);

  const filteredAccounts = useMemo(() => {
    const all = (dashboardAccounts || []) as any[];
    if (filteredAccountIds !== null) {
      return all.filter((a: any) => filteredAccountIds.has(a.id));
    }
    return all;
  }, [dashboardAccounts, filteredAccountIds]);

  const calendarEvents = useMemo<CalendarEvent[]>(() => {
    const events: CalendarEvent[] = [];
    const now = new Date();
    for (const acc of filteredAccounts) {
      events.push(...buildAccountCalendarEvents(acc, CURRENCY));
    }
    const chargeList = charges as {
      id: string;
      amount: number;
      frequency: string;
      accountId?: string | null;
      name: string;
      createdAt: Date | string;
      startDate?: Date | string | null;
      endDate?: Date | string | null;
      billingDay?: number | null;
    }[];
    for (const charge of chargeList) {
      if (charge.accountId) {
        const belongs = filteredAccounts.some((a: any) => a.id === charge.accountId);
        if (!belongs) continue;
      }
      // Même règle que les KPIs (chargeSchedule) : un abonnement fermé ne
      // projette plus aucun prélèvement après sa date de fin.
      const eventType = charge.frequency === "one_time" ? "expense" : "recurring";
      for (const { date, amount } of chargeCalendarInstances(charge, now)) {
        events.push({ date, type: eventType, amount, currency: CURRENCY, label: charge.name });
      }
    }
    return events;
  }, [filteredAccounts, charges]);

  const dashboardData: DashboardData = {
    trades,
    routines: routines || [],
    goals: goals || [],
    accounts: filteredAccounts as any[],
    groups: (dashboardGroupsProp || []) as any[],
    strategies: (dashboardStrategiesProp || []) as any[],
    sessions: (sessions || []) as any[],
    metrics,
    statsTrades: statsTradesList,
    statsTradesList,
    equityCurveData: equityCurveData as any,
    accountEvents,
    fundedEvents,
    payoutEvents,
    violationsByDate,
    pendingRoutines,
    topGoals,
    dateRange,
    accountFilter,
    setAccountFilter,
    strategyIds,
    setStrategyIds,
    statsPeriod: "30d" as const,
    statsStartDate: statsStartDate ?? null,
    statsEndDate: statsEndDate ?? null,
    setSelectedDay,
    handleCompleteRoutine: (r: any) => onCompleteRoutine?.(r),
    setSelectedMonth,
    isDark,
    themeId,
    isFreePlan,
    readOnly,
    hasMentor,
    t,
    chartMode,
    setChartMode: (m: "pnl" | "networth") => {
      setChartMode(m);
      localStorage.setItem("dashboard_chart_mode", m);
    },
    onAddTrade: () => onAddTrade?.(),
    onPromoteToFunded: (a: any) => onPromoteToFunded?.(a),
    onOpenPlanModal: () => onOpenPlanModal?.(),
    analyticsData,
    charges,
    tptWalletContext,
    filteredAccounts,
    calendarEvents,
    pfmTemplateFilter,
    setPfmTemplateFilter,
    accountStatusFilter,
    setAccountStatusFilter,
    setDateRange,
    calendarMonth,
    setCalendarMonth,
    showImportModal,
    setShowImportModal,
    showAddPayoutModal,
    setShowAddPayoutModal,
    isLoadingPfm: isLoadingPfm ?? false,
    userId: authUser?.id,
    hiddenAccountsCount: 0,
    showPlanModal: false,
    setShowPlanModal: () => {},
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [layoutLibraryOpen, setLayoutLibraryOpen] = useState(false);
  const isDuplicatingRef = useRef(false);
  const { activeLayout: libraryActiveLayout, duplicate, activate } = useLayoutsLibrary("dashboard");
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [pickerContext, setPickerContext] = useState<{
    colStart: number;
    rowStart: number;
    maxColSpan: number;
    context?: "topbar" | "grid";
  } | null>(null);
  const resetLayoutFn = useAction(resetLayoutContent);
  const isMobile = useIsMobile();

  return (
    <PageTransition className="w-full">
      {/* Upgrade Banner for Free Plan Users */}
      <AnimatePresence>
        {isFreePlan && !showPlanModal && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="mb-8"
          >
            <Card className="border-primary/50 bg-gradient-to-r from-primary/5 via-background to-primary/5">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="rounded-full bg-primary/10 p-3">
                      <Sparkles className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold">
                        {t.dashboard.upgradeTitle}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t.dashboard.upgradeDescription}
                      </p>
                    </div>
                  </div>
                  <BeamButton onClick={() => onOpenPlanModal?.()}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    {t.dashboard.upgradeButton}
                  </BeamButton>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="sticky top-0 z-30 -mx-3 sm:-mx-5 mb-5 flex items-center justify-between gap-4 min-h-10 border-b border-border bg-background/80 px-3 sm:px-5 py-3 backdrop-blur-md"
      >
        <h1 className="flex items-center gap-2.5 text-lg font-semibold shrink-0">
          {t.dashboard.title}
        </h1>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isEditMode && !readOnly ? (
            <TopbarDndWrapper
              slots={topbarSlots}
              onSwap={(idA, idB) => { swapWidgets(idA, idB); flushSave(); }}
            >
              {topbarSlots.map((slot) => (
                <SortableTopbarSlot
                  key={slot.id}
                  id={slot.id}
                  slot={slot}
                  isEditMode={true}
                  dateRange={dateRange}
                  accountFilter={accountFilter}
                  strategyIds={strategyIds}
                  accounts={(dashboardAccounts || []) as any[]}
                  groups={(dashboardGroupsProp || []) as any[]}
                  strategies={(dashboardStrategiesProp || []) as any[]}
                  onDateRangeChange={setDateRange}
                  onAccountFilterChange={setAccountFilter}
                  onStrategyIdsChange={setStrategyIds}
                  onAddTrade={() => onAddTrade?.()}
                  onAddPayout={() => {}}
                  onRemove={() => { removeWidget(slot.id); trackWidgetRemoved(slot.widgetKey); flushSave(); }}
                  onPin={readOnly ? undefined : (value) => {
                    updateSlotDefaultFilterValue(slot.id, value);
                    flushSave();
                    toast({ description: value === null ? t.pages.unpinDefault : t.pages.pinAsDefaultConfirm, duration: 1500 });
                  }}
                  pfmTemplateFilter={pfmTemplateFilter}
                  onPfmTemplateFilterChange={setPfmTemplateFilter}
                  accountStatusFilter={accountStatusFilter}
                  onAccountStatusFilterChange={setAccountStatusFilter}
                />
              ))}
            </TopbarDndWrapper>
          ) : (
            <TopbarFilterBar
              slots={topbarSlots}
              dateRange={dateRange}
              accountFilter={accountFilter}
              strategyIds={strategyIds}
              accounts={(dashboardAccounts || []) as any[]}
              groups={(dashboardGroupsProp || []) as any[]}
              strategies={(dashboardStrategiesProp || []) as any[]}
              onDateRangeChange={setDateRange}
              onAccountFilterChange={setAccountFilter}
              onStrategyIdsChange={setStrategyIds}
              onAddTrade={() => onAddTrade?.()}
              onAddPayout={() => {}}
              onRemove={() => {}}
              onPin={undefined}
              pfmTemplateFilter={pfmTemplateFilter}
              onPfmTemplateFilterChange={setPfmTemplateFilter}
              accountStatusFilter={accountStatusFilter}
              onAccountStatusFilterChange={setAccountStatusFilter}
              extraActions={!readOnly ? (
                <>
                  {/* Edgy sits with the page's other toolbar actions. Read-only
                      (shared layout) views get the whole block hidden — a visitor
                      has nothing to analyse there. */}
                  <EdgyHeaderButton />
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-8 gap-1 max-w-[140px]"
                    onClick={() => setLayoutLibraryOpen(true)}
                  >
                    <Layers className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{(libraryActiveLayout as any)?.name ?? t.dashboard.layouts.menuTitle}</span>
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  </Button>
                  {!isEditMode && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs h-8 min-w-[5rem]"
                      disabled={isDuplicating}
                      onClick={async () => {
                        if (isFreePlan) {
                          onOpenPlanModal?.();
                          return;
                        }
                        if ((libraryActiveLayout as any)?.isReadOnly) {
                          if (isDuplicatingRef.current) return;
                          isDuplicatingRef.current = true;
                          setIsDuplicating(true);
                          let success = false;
                          try {
                            const created = await duplicate((libraryActiveLayout as any).id, t.dashboard.layouts.newLayout);
                            if (created) {
                              const activated = await activate((created as any).id);
                              if (activated) success = true;
                            }
                          } finally {
                            setIsDuplicating(false);
                            isDuplicatingRef.current = false;
                          }
                          if (!success) return;
                          await refetchLayout();
                        }
                        setIsEditMode(true);
                      }}
                    >
                      {isDuplicating ? (
                        <><Loader2 className="h-3 w-3 animate-spin" />{t.dashboard.customize}</>
                      ) : (
                        t.dashboard.customize
                      )}
                    </Button>
                  )}
                </>
              ) : undefined}
            />
          )}
          {isEditMode && !readOnly && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => setPickerContext({ colStart: 1, rowStart: 1, maxColSpan: 6, context: "topbar" })}
            >
              {t.pages.topbarAdd}
            </Button>
          )}
          {!readOnly && (
            <LayoutLibraryModal
              open={layoutLibraryOpen}
              onOpenChange={setLayoutLibraryOpen}
              scope="dashboard"
              isFreePlan={isFreePlan}
              onUpgradeClick={() => onOpenPlanModal?.()}
              activeLayoutId={(libraryActiveLayout as any)?.id ?? null}
              onNewLayoutActivated={async () => {
                await refetchLayout();
                setIsEditMode(true);
              }}
              onBeforeActivate={async () => {
                await flushSave();
              }}
            />
          )}
        </div>
      </motion.div>

      {isEditMode && (
        <EditModeBar
          onReset={() => setShowResetDialog(true)}
          onDone={async () => { await compactAndFlushSave(); trackLayoutSaved(); setIsEditMode(false); }}
        />
      )}

      {(() => {
        const hasPfmWidgets = layout.slots.some((s) => PROPFIRM_ONLY_WIDGET_KEYS.includes(s.widgetKey));
        const handleRemoveWidget = (slotId: string) => {
          const slot = layout.slots.find((s) => s.id === slotId);
          if (slot) trackWidgetRemoved(slot.widgetKey);
          removeWidget(slotId);
        };
        const grid = (
          <WidgetDataProvider value={dashboardData}>
            <DashboardLayout
              layout={layout}
              isEditMode={isEditMode}
              isMobile={isMobile}
              onAddWidgetClick={(colStart, rowStart, maxColSpan) =>
                setPickerContext({ colStart, rowStart, maxColSpan })
              }
              onRemoveWidget={handleRemoveWidget}
              onWidgetSizeChange={setWidgetSize}
              onMoveWidget={moveWidget}
              onSwapWidgets={swapWidgets}
              onUpdateSlotConfig={updateSlotDefaultFilterValue}
              onUpdateSlotViewMode={updateSlotViewMode}
            />
          </WidgetDataProvider>
        );
        return grid;
      })()}

      <AddWidgetPicker
        open={pickerContext !== null}
        onOpenChange={(open) => { if (!open) setPickerContext(null); }}
        layout={layout}
        maxColSpan={pickerContext?.maxColSpan ?? 1}
        rowStart={pickerContext?.rowStart ?? 1}
        colStart={pickerContext?.colStart ?? 1}
        scope="dashboard"
        initialCategory={pickerContext?.context === "topbar" ? "topbar" : undefined}
        hasMentor={hasMentor}
        isFreePlan={isFreePlan}
        onUpgradeClick={() => onOpenPlanModal?.()}
        onPick={(widgetKey) => {
          if (!pickerContext) return;
          if (TOPBAR_WIDGET_KEYS.has(widgetKey)) {
            addWidget(widgetKey, 1, 0, 1, 1, "topbar");
          } else {
            const maxOccupiedRow = layout.slots
              .filter((s) => s.placement !== "topbar")
              .reduce((m, s) => Math.max(m, s.rowStart + s.rowSpan - 1), 0);
            addWidget(widgetKey, pickerContext.colStart, maxOccupiedRow + 1, pickerContext.maxColSpan);
          }
          trackWidgetAdded(widgetKey);
          setPickerContext(null);
        }}
      />

      <ResetLayoutDialog
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
        onConfirm={async () => {
          const layoutId = (serverLayout as any)?.id as string | undefined;
          if (layoutId) {
            await resetLayoutFn({ layoutId });
            await refetchLayout();
          }
          setIsEditMode(false);
        }}
      />



      {/* Day Detail Modal */}
      <DayDetailModal
        date={selectedDay}
        open={selectedDay !== null}
        onOpenChange={(open) => { if (!open) setSelectedDay(null); }}
        trades={trades}
        sessions={sessions || []}
        routines={routines || []}
        goals={goals || []}
        accounts={(dashboardAccounts || []) as any[]}
        groups={(dashboardGroupsProp || []) as { id: string; name: string }[]}
        accountEvents={accountEvents}
        fundedEvents={fundedEvents}
        payoutEvents={payoutEvents}
        violationsByDate={violationsByDate}
        totalsArePartial={totalsArePartial}
        onDeleted={readOnly ? undefined : () => setSelectedDay(null)}
      />
    </PageTransition>
  );
}
